import { useState, useEffect } from "react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import type { PersonaListItem } from "./personas-tab-types";

interface PersonaFormDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  editingPersona?: (PersonaListItem & { disposition?: string | null }) | null;
}

export function PersonaFormDialog({
  projectId,
  open,
  onClose,
  editingPersona,
}: PersonaFormDialogProps) {
  const utils = trpc.useUtils();
  const isEditing = Boolean(editingPersona?.id);

  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [brief, setBrief] = useState("");
  const [background, setBackground] = useState("");
  const [disposition, setDisposition] = useState("");

  useEffect(() => {
    if (editingPersona) {
      setName(editingPersona.name);
      setRole(editingPersona.role);
      setBrief(editingPersona.brief ?? "");
      setBackground(editingPersona.context ?? "");
      setDisposition(
        (editingPersona as { disposition?: string | null })?.disposition ?? "",
      );
    } else {
      setName("");
      setRole("");
      setBrief("");
      setBackground("");
      setDisposition("");
    }
  }, [editingPersona, open]);

  const resetForm = () => {
    setName("");
    setRole("");
    setBrief("");
    setBackground("");
    setDisposition("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const createMutation = trpc.persona.create.useMutation({
    onSuccess: () => {
      toast.success("Persona created");
      utils.persona.list.invalidate({ projectId });
      handleClose();
    },
    onError: (err) => toastTrpcError(err, "Failed to create persona"),
  });

  const updateMutation = trpc.persona.update.useMutation({
    onSuccess: () => {
      toast.success("Persona updated");
      utils.persona.list.invalidate({ projectId });
      handleClose();
    },
    onError: (err) => toastTrpcError(err, "Failed to update persona"),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    if (editingPersona?.id) {
      updateMutation.mutate({
        id: editingPersona.id,
        name: name.trim(),
        role: role.trim(),
        brief: brief.trim() || null,
        context: background.trim() || null,
        disposition: disposition.trim() || null,
      });
    } else {
      createMutation.mutate({
        projectId,
        name: name.trim(),
        role: role.trim(),
        brief: brief.trim() || undefined,
        context: background.trim() || undefined,
        disposition: disposition.trim() || undefined,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit persona" : "Create persona"}
          </DialogTitle>
          <DialogDescription>
            Define a domain-grounded character for experience testing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="persona-name">Name</Label>
            <Input
              id="persona-name"
              placeholder="e.g. Lars Eriksson"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="persona-role">Role</Label>
            <Input
              id="persona-role"
              placeholder="e.g. Livestock farmer with 50 cattle"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="persona-brief">
              Brief{" "}
              <span className="text-muted-foreground font-normal">
                (optional — one-line summary shown in lists and pickers)
              </span>
            </Label>
            <Input
              id="persona-brief"
              placeholder="Shown in lists and pickers"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="persona-background">
              Background{" "}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Durable working reality, environment/device context, and what they
              use today — never a one-off task.
            </p>
            <Textarea
              id="persona-background"
              rows={4}
              placeholder="e.g. Travels to client sites by train 2–3 days a week, files expenses from a laptop on Friday afternoons, keeps paper receipts in a jacket pocket."
              value={background}
              onChange={(e) => setBackground(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="persona-disposition">
              Disposition{" "}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Habits, patience level, and what makes them hesitate or abandon a
              flow.
            </p>
            <Textarea
              id="persona-disposition"
              rows={2}
              placeholder="e.g. Careful, avoids asking colleagues for help, abandons forms that require accounting codes."
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !role.trim() || isPending}
          >
            {isPending ? "Saving..." : isEditing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
